/** What the gear menu shows, built fresh from whatever the robot last said. */
import type { Driver, PathState, Settings, Status } from "../../adamo/types";
import { SETTING_LIMITS } from "../../adamo/types";
import { linkLine } from "../../adamo/perfText";
import { operatorId } from "../../state/operatorId";
import type { Widget } from "./widgets";

export type View = {
  settings: Settings;
  path: PathState;
  driver: Driver;
  status: Status | null;
  convergence: number;
  swapEyes: boolean;
  flatten: boolean;
  verticalOffset: number;
  /** The box map beside the picture. */
  showMap: boolean;
  /** Whether this arm speaks the settings protocol; an older one takes none of them. */
  settingsUnderstood: boolean;
  /** Whether what is arriving is a side-by-side pair at all. */
  sourceIsStereo: boolean;
};

const mm = (v: number | null | undefined) => (v === null || v === undefined ? "—" : `${Math.round(v)} mm`);

export function buildMenu(view: View): Widget[] {
  const { settings, path, status, driver } = view;
  const mine = driver?.lease_ok === true && driver.operator_id === operatorId();
  const theirs = driver?.lease_ok === true && !mine;
  const widgets: Widget[] = [
    { kind: "button", id: "close_menu", label: "Close  ·  the controls drive the arm again",
      tone: "primary" },
    { kind: "section", text: "Settings" },
    { kind: "slider", id: "gripper_open", label: "Default gripper width",
      text: `${Math.round(settings.gripper_open * 100)}%`, value: settings.gripper_open,
      ...SETTING_LIMITS.gripper_open },
    { kind: "slider", id: "speed", label: "Arm movement speed",
      text: `${Math.round(settings.speed * 1000)} mm/s`, value: settings.speed, ...SETTING_LIMITS.speed },
    { kind: "slider", id: "grip_close_speed", label: "Gripper close speed",
      text: settings.grip_close_speed <= 0 ? "at once" : `${(1 / settings.grip_close_speed).toFixed(1)} s`,
      value: settings.grip_close_speed, ...SETTING_LIMITS.grip_close_speed },
    { kind: "choice", id: "mode", label: "Control type", value: settings.mode,
      options: [{ value: "crane", label: "Joysticks" }, { value: "pose", label: "Motion" }] },
    { kind: "choice", id: "overlay", label: "Depth cues on the picture",
      value: settings.overlay ? "on" : "off",
      options: [{ value: "on", label: "Shown" }, { value: "off", label: "Hidden" }] },
  ];
  if (settings.mode === "pose") {
    widgets.push({ kind: "choice", id: "hand", label: "Dominant arm", value: settings.hand,
                   options: [{ value: "left", label: "Left" }, { value: "right", label: "Right" }] });
  }

  // The path's own buttons live on the automation panel (automationModel.ts), which leaves the
  // controls driving the arm: from here, every point was shut the menu, move, open it, press.
  widgets.push(
    { kind: "section", text: "Automation" },
    { kind: "note", label: "taught",
      value: `${path.start ? "start" : "no start"} · ${path.waypoints} waypoint${path.waypoints === 1 ? "" : "s"} · ${path.end ? "end" : "no end"}` },
    { kind: "button", id: "open_automation", label: "Create Automation", tone: "primary" },
  );

  if (status && !view.settingsUnderstood) {
    widgets.push(
      { kind: "note", label: "settings",
        value: "this arm ignores them (older program)" },
    );
  }
  widgets.push({ kind: "section", text: "The arm" });
  if (status) {
    widgets.push(
      { kind: "note", label: "state", value: `${status.state}${status.paused ? " · paused" : ""}${status.confirm_left_s != null ? ` · ${status.confirm_left_s.toFixed(1)} s` : ""}` },
      { kind: "note", label: "gripper", value: status.gripper_closed ? "closed" : `open to ${mm(status.gripper_open_mm)}` },
    );
    if (status.highlight) {
      widgets.push({ kind: "note", label: "above the pile",
                     value: status.highlight.low ? "LOW ENOUGH" : mm(status.highlight.top_below_fingers_mm) });
    }
    if (status.rim && status.rim.level !== "clear") {
      widgets.push({ kind: "note", label: "camera vs box", value: `${status.rim.level.toUpperCase()} · ${status.rim.say}` });
    }
    if (status.message) widgets.push({ kind: "note", label: "says", value: status.message });
  } else {
    widgets.push({ kind: "note", label: "state", value: "waiting for the robot" });
  }
  widgets.push(
    { kind: "note", label: "driving", value: mine ? "you" : theirs ? (driver?.display_name || "someone else") : "nobody" },
  );
  if (status?.perf) widgets.push({ kind: "note", label: "link", value: linkLine(status.perf) });
  if (theirs) widgets.push({ kind: "button", id: "take_over", label: "Take over", tone: "warn" });

  widgets.push(
    { kind: "section", text: "Do" },
    { kind: "button", id: "enable", label: "Enable the arm", disabled: status?.state !== "idle" },
    { kind: "button", id: "picked", label: status?.state === "confirm" ? "Deliver it" : "Picked it up", tone: "primary",
      disabled: status?.state !== "pick" && status?.state !== "confirm" },
    { kind: "button", id: "reset", label: "Start this pick again", disabled: status?.state !== "pick" },
    { kind: "button", id: "release", label: "Release the arm", tone: "warn" },
  );

  const solid = view.sourceIsStereo && !view.flatten;
  widgets.push(
    { kind: "section", text: "The view" },
    { kind: "choice", id: "flatten", label: "Camera view",
      value: view.flatten ? "flat" : "stereo",
      options: [{ value: "stereo", label: "Stereoscopic" }, { value: "flat", label: "One screen" }] },
  );
  if (!view.sourceIsStereo) {
    widgets.push({ kind: "note", label: "", value: "this camera sends one picture, so it is flat either way" });
  }
  widgets.push(
    { kind: "choice", id: "showMap", label: "Box map, left of the picture",
      value: view.showMap ? "on" : "off",
      options: [{ value: "on", label: "Shown" }, { value: "off", label: "Hidden" }] },
  );
  widgets.push(
    { kind: "note", label: "", value: "tap X to shut this · hold X to roll the gripper" },
    { kind: "button", id: "recentre", label: "Re-centre the screen" },
  );
  if (solid) {
    // The two knobs that decide whether a pair can be looked at for an hour. Separation is
    // the horizontal offset between the halves -- more of it pushes the scene away, less
    // brings it towards you, and it is the one people mean by eye spacing. Alignment is the
    // vertical offset, which should be nothing: eyes will not fuse a pair that is out of
    // line up and down, however good the separation is.
    widgets.push(
      { kind: "slider", id: "convergence", label: "Eye separation",
        text: `${Math.round(view.convergence)} px`, value: view.convergence,
        min: -120, max: 120, step: 2 },
      { kind: "slider", id: "verticalOffset", label: "Vertical alignment",
        text: view.verticalOffset === 0 ? "level" : `${Math.round(view.verticalOffset)} px`,
        value: view.verticalOffset, min: -40, max: 40, step: 1 },
      { kind: "button", id: "reset_stereo", label: "Back to no offset at all" },
    );
  }
  widgets.push(
    { kind: "choice", id: "swap", label: solid ? "Swap the eyes" : "Which eye to show",
      value: view.swapEyes ? "yes" : "no",
      options: [{ value: "no", label: solid ? "Normal" : "Left" }, { value: "yes", label: solid ? "Swapped" : "Right" }] },
  );
  return widgets;
}
